import type {MobileAlgoContext, ReadOnlyExtensionAPI} from './types'
import type {ExtensionAPI} from '@wix/document-manager-core'
import type {RMApi} from '../rendererModel'
import type {StructureExtensionAPI} from '../structure'
import type {Pointer} from '@wix/document-services-types'
import type {ThemeExtAPI} from '../theme/theme'
import type {PageExtensionAPI} from '../page'
import type {MobileAlgoExtensionApi} from './mobileAlgo'
import type {ComponentsMetadataAPI} from '../componentsMetadata/componentsMetadata'
import type {AccessibilityExtensionAPI} from '../accessibility/types'
import type {LayoutExtensionApi} from '../layout/layout'
import type {ManifestExtensionAPI} from '../builderComponents/manifest'
import {isHeaderSection} from '../componentsMetadata/utils/pageUtils'
import type {DataModelExtensionAPI} from '../dataModel/dataModel'
import {DATA_TYPES} from '../../constants/constants'
import type {EnvironmentExtensionAPI} from '../environment'

export const createReadOnlyExtensionApi = (extensionAPI: ExtensionAPI): ReadOnlyExtensionAPI => {
    return {
        getInstance: () => (extensionAPI as RMApi).siteAPI.getInstance(),
        getChildren: (componentPointer: Pointer) =>
            (extensionAPI as StructureExtensionAPI).components.getChildren(componentPointer),
        isTemplate: () => (extensionAPI as RMApi).siteAPI.isTemplate(),
        getTextTheme: () => (extensionAPI as ThemeExtAPI).theme.getTextTheme(),
        getMetaSiteId: () => (extensionAPI as RMApi).rendererModel.getMetaSiteId(),
        getSiteId: () => (extensionAPI as RMApi).rendererModel.getSiteId(),
        getMainPageId: () => (extensionAPI as PageExtensionAPI).page.getMainPageId(),
        getPluginsApi: (ctx: MobileAlgoContext) =>
            (extensionAPI as MobileAlgoExtensionApi).mobileAlgo.plugins.getApis(ctx),
        components: {
            isPinned: (componentPointer: Pointer) =>
                (extensionAPI as LayoutExtensionApi).components.isPinned(componentPointer),
            isHeaderSection: (componentPointer: Pointer) => isHeaderSection(extensionAPI, componentPointer)
        },
        isFullWidth: (componentPointer: Pointer) =>
            (extensionAPI as ComponentsMetadataAPI).componentsMetadata.isFullWidth(componentPointer),
        isRTL: () =>
            (extensionAPI as AccessibilityExtensionAPI).accessibility.languageDirection.getSiteDirection() === 'rtl',
        manifest: (extensionAPI as ManifestExtensionAPI).manifest,
        getMainTextField: (componentType: string, data: any) =>
            (extensionAPI as ManifestExtensionAPI).manifest.getMainTextField(componentType, data),
        isDuplicatedComponent: (compPointer: Pointer, rootPointer: Pointer): boolean => {
            const {dataModel} = extensionAPI as DataModelExtensionAPI
            const editorSettings = dataModel.components.getItem(rootPointer, DATA_TYPES.editorsettings)
            return !!editorSettings?.linkedComponents?.[compPointer.id]
        },
        isClonedComponent: (compPointer: Pointer, rootPointer: Pointer): boolean => {
            const {dataModel} = extensionAPI as DataModelExtensionAPI
            const editorSettings = dataModel.components.getItem(rootPointer, DATA_TYPES.editorsettings)
            return (
                !!editorSettings?.linkedComponents &&
                Object.values(editorSettings.linkedComponents).includes(compPointer.id)
            )
        },
        experiments: {
            isOpen: (experiment: string) =>
                (extensionAPI as EnvironmentExtensionAPI).environment.isExperimentOpen(experiment)
        }
    }
}
